import { createRootRoute, createRoute, Outlet, redirect } from '@tanstack/react-router';

import { useAuthStore } from './auth/auth-store.js';
import { LoginPage } from './auth/login-page.js';
import { ResetPasswordPage } from './auth/reset-password-page.js';
import { ThreeColumnLayout } from './layout/three-column.js';
import { AnalyticsPage } from './pages/analytics-page.js';
import { BotFlowPage } from './pages/bot-flow-page.js';
import { BotsPage } from './pages/bots-page.js';
import { ContactsPage } from './pages/contacts-page.js';
import { EmbedPage } from './pages/embed-page.js';
import { InboxPage } from './pages/inbox-page.js';
import { OnboardingPage } from './pages/onboarding-page.js';
import { ProfilePage } from './pages/profile-page.js';
import { SettingsApiKeys } from './pages/settings/api-keys.js';
import { SettingsAuditLog } from './pages/settings/audit-log.js';
import { SettingsBilling } from './pages/settings/billing.js';
import { SettingsChannels } from './pages/settings/channels.js';
import { SettingsDataConnectors } from './pages/settings/data-connectors.js';
import { SettingsGdpr } from './pages/settings/gdpr.js';
import { SettingsGeneral } from './pages/settings/general.js';
import { SettingsLicense } from './pages/settings/license.js';
import { SettingsRoles } from './pages/settings/roles.js';
import { SettingsSecurity } from './pages/settings/security.js';
import { SettingsSla } from './pages/settings/sla.js';
import { SettingsStores } from './pages/settings/stores.js';
import { SettingsTeam } from './pages/settings/team.js';
import { SettingsTemplates } from './pages/settings/templates.js';
import { SettingsWebhooks } from './pages/settings/webhooks.js';
import { SettingsLayout } from './pages/settings-layout.js';
import { TicketsPage } from './pages/tickets-page.js';
import { WidgetsPage } from './pages/widgets-page.js';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

/**
 * Встроенный режим для кабинета партнёра.
 *
 * Висит на корне, а не под dashboard: там `beforeLoad` уводит на страницу
 * входа, а здесь вход как раз и выполняется — обменом одноразового билета.
 * Отдельный адрес нужен и серверу: только для `/embed` разрешено показывать
 * нас в чужой рамке (см. deploy/nginx и vite.config).
 */
const embedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/embed',
  component: EmbedPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'dashboard',
  beforeLoad: ({ location }) => {
    // Запоминаем, куда шли: ссылка «Подключить чат» из кабинета платформы
    // ведёт на страницу настроек, и после входа нужно вернуться именно туда.
    if (!useAuthStore.getState().user) {
      throw redirect({ to: '/login', search: { redirect: location.href } as never });
    }
  },
  component: ThreeColumnLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/',
  component: InboxPage,
});
const inboxRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/inbox',
  component: InboxPage,
});
const ticketsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/tickets',
  component: TicketsPage,
});
const contactsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/contacts',
  component: ContactsPage,
});
const analyticsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/analytics',
  component: AnalyticsPage,
});
const botsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/bots',
  component: BotsPage,
});
const botFlowRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/bots/$scenarioId',
  component: BotFlowPage,
});
const widgetsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/widgets',
  component: WidgetsPage,
});
const profileRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/profile',
  component: ProfilePage,
});

const settingsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  id: 'settings-shell',
  component: SettingsLayout,
});
const settingsIndex = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings',
  component: SettingsGeneral,
});
const settingsGeneral = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/general',
  component: SettingsGeneral,
});
const settingsTeam = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/team',
  component: SettingsTeam,
});
const settingsRoles = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/roles',
  component: SettingsRoles,
});
const settingsChannels = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/channels',
  component: SettingsChannels,
});
const settingsStores = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/stores',
  component: SettingsStores,
});
const settingsDataConnectors = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/data-connectors',
  component: SettingsDataConnectors,
});
const settingsSecurity = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/security',
  component: SettingsSecurity,
});
const settingsAudit = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/audit-log',
  component: SettingsAuditLog,
});
const settingsGdpr = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/gdpr',
  component: SettingsGdpr,
});
const settingsBilling = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/billing',
  component: SettingsBilling,
});
const settingsLicense = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/license',
  component: SettingsLicense,
});
const settingsApiKeys = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/api-keys',
  component: SettingsApiKeys,
});
const settingsWebhooks = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/webhooks',
  component: SettingsWebhooks,
});
const settingsTemplates = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/templates',
  component: SettingsTemplates,
});
const settingsSla = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/settings/sla',
  component: SettingsSla,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: OnboardingPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  onboardingRoute,
  embedRoute,
  dashboardRoute.addChildren([
    indexRoute,
    inboxRoute,
    ticketsRoute,
    contactsRoute,
    analyticsRoute,
    botsRoute,
    botFlowRoute,
    widgetsRoute,
    profileRoute,
    settingsRoute.addChildren([
      settingsIndex,
      settingsGeneral,
      settingsTeam,
      settingsRoles,
      settingsChannels,
      settingsStores,
      settingsDataConnectors,
      settingsSecurity,
      settingsAudit,
      settingsGdpr,
      settingsBilling,
      settingsLicense,
      settingsApiKeys,
      settingsWebhooks,
      settingsTemplates,
      settingsSla,
    ]),
  ]),
]);
